import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart3 } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from 'recharts';
import { TAG_COLORS } from '@/utils/behavioural';

const BACKEND_URL = '';

const COLORS = ['#3E60B1', '#9BC269', '#6C9BEB', '#9EDE4B', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#64748b', '#dc2626', '#06b6d4'];

export function AdminAnalyticsTab() {
  const [selectedLab, setSelectedLab] = useState('all');
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [bannerAnalytics, setBannerAnalytics] = useState(null);
  const [labInsights, setLabInsights] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/admin/analytics`, { credentials: 'include' });
        if (res.ok) setAnalytics(await res.json());
        fetch(`${BACKEND_URL}/api/admin/banners/analytics`, { credentials: 'include' })
          .then(r => r.ok ? r.json() : null).then(d => d && setBannerAnalytics(d)).catch(() => {});
        fetch(`${BACKEND_URL}/api/admin/analytics/britelabs-insights`, { credentials: 'include' })
          .then(r => r.ok ? r.json() : null).then(d => d && setLabInsights(d)).catch(() => {});
      } catch {}
      setAnalyticsLoading(false);
    };
    load();
  }, []);

  if (analyticsLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <Card className="p-8 text-center">
        <BarChart3 className="h-10 w-10 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500">Unable to load analytics data</p>
      </Card>
    );
  }

  return (
    <>
      {/* Overview Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: 'Users', value: analytics.overview.total_users, color: 'text-brand-navy' },
          { label: 'Events', value: analytics.overview.total_events, color: 'text-brand-blue' },
          { label: 'Courses', value: analytics.overview.total_courses, color: 'text-brand-sage' },
          { label: 'Labs', value: analytics.overview.total_labs, color: 'text-purple-600' },
          { label: 'Companies', value: analytics.overview.total_companies, color: 'text-amber-600' },
          { label: 'Sponsors', value: analytics.overview.total_sponsors, color: 'text-green-600' },
        ].map(s => (
          <Card key={s.label} className="p-4 text-center" data-testid={`analytics-stat-${s.label.toLowerCase()}`}>
            <p className={`text-3xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-slate-500 font-medium mt-1">{s.label}</p>
          </Card>
        ))}
      </div>

      {/* Row 1: Growth + Role Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5" data-testid="analytics-user-growth">
          <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4">User Growth (30 Days)</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={analytics.user_growth}>
                <defs>
                  <linearGradient id="growthGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3E60B1" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#3E60B1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                <Area type="monotone" dataKey="cumulative" stroke="#3E60B1" fill="url(#growthGrad)" strokeWidth={2} name="Total Users" />
                <Bar dataKey="count" fill="#9BC269" radius={[4, 4, 0, 0]} name="New Signups" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5" data-testid="analytics-role-distribution">
          <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4">Role Distribution</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={analytics.role_distribution} cx="50%" cy="45%" innerRadius={45} outerRadius={75} dataKey="value" paddingAngle={2}>
                  {analytics.role_distribution.map((_, i) => (
                    <Cell key={i} fill={['#3E60B1', '#9BC269', '#6C9BEB', '#9EDE4B'][i % 4]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v, name) => [v, name]} />
                <Legend verticalAlign="bottom" iconType="circle" formatter={(value) => <span className="text-xs text-slate-600">{value}</span>} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Row 2: Feedback Rating Distribution + Module Ratings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5" data-testid="analytics-rating-distribution">
          <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4">Feedback Ratings</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analytics.rating_distribution}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="rating" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]} name="Responses">
                  {analytics.rating_distribution.map((_, i) => (
                    <Cell key={i} fill={['#ef4444', '#f97316', '#eab308', '#84cc16', '#22c55e'][i]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5" data-testid="analytics-module-ratings">
          <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4">Module Ratings</h3>
          {analytics.module_avg_ratings.length > 0 ? (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics.module_avg_ratings} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" domain={[0, 5]} tick={{ fontSize: 11 }} />
                  <YAxis dataKey="module" type="category" tick={{ fontSize: 11 }} width={100} />
                  <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} formatter={(v) => [`${v} / 5`, 'Avg Rating']} />
                  <Bar dataKey="avg_rating" fill="#3E60B1" radius={[0, 6, 6, 0]} name="Avg Rating" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-56 flex items-center justify-center text-slate-400 text-sm">No module ratings yet</div>
          )}
        </Card>
      </div>

      {/* Row 3: Course Enrollment + BriteLabs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5" data-testid="analytics-course-enrollment">
          <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4">Course Enrollment</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analytics.course_enrollment} margin={{ bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="course" tick={{ fontSize: 10, angle: -25, textAnchor: 'end' }} interval={0} height={60} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                <Bar dataKey="enrolled" fill="#9BC269" radius={[6, 6, 0, 0]} name="Enrolled" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5" data-testid="analytics-britelabs">
          <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4">BriteLabs Overview</h3>
          <div className="grid grid-cols-2 gap-4 h-56">
            <div>
              <p className="text-xs text-slate-500 mb-2 font-medium">By Status</p>
              {analytics.lab_statuses.length === 0 ? (
                <div className="h-full flex items-center justify-center text-xs text-slate-400">No labs yet</div>
              ) : analytics.lab_statuses.length === 1 ? (
                <div className="h-full flex flex-col items-center justify-center">
                  <div className="text-3xl font-bold text-brand-navy tabular-nums">{analytics.lab_statuses[0].value}</div>
                  <div className="text-xs text-slate-500 mt-1 capitalize">{analytics.lab_statuses[0].name} lab{analytics.lab_statuses[0].value === 1 ? '' : 's'}</div>
                  <div className="text-[10px] text-slate-400 mt-2 px-2 text-center">More variety appears once labs progress through statuses</div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="90%">
                  <PieChart>
                    <Pie data={analytics.lab_statuses} cx="50%" cy="50%" outerRadius={60} dataKey="value" label={({ name, value }) => `${name} (${value})`}>
                      {analytics.lab_statuses.map((_, i) => (
                        <Cell key={i} fill={['#22c55e', '#eab308', '#3E60B1', '#ef4444', '#6C9BEB'][i % 5]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-2 font-medium">By Category</p>
              {analytics.lab_categories.length === 0 ? (
                <div className="h-full flex items-center justify-center text-xs text-slate-400">No labs yet</div>
              ) : (
                <div className="space-y-2 mt-1">
                  {analytics.lab_categories.map((cat, i) => (
                    <div key={cat.name} className="flex items-center justify-between">
                      <span className="text-xs text-slate-700 truncate flex-1">{cat.name}</span>
                      <div className="flex items-center gap-2 ml-2">
                        <div className="w-16 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${(cat.value / Math.max(...analytics.lab_categories.map(c => c.value))) * 100}%`, backgroundColor: ['#3E60B1', '#9BC269', '#6C9BEB', '#9EDE4B'][i % 4] }} />
                        </div>
                        <span className="text-xs font-medium text-slate-600 w-5 text-right">{cat.value}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* Row 4: Event RSVPs */}
      {analytics.event_rsvps.length > 0 && (
        <Card className="p-5" data-testid="analytics-event-rsvps">
          <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4">Event RSVPs</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analytics.event_rsvps}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="event" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                <Bar dataKey="rsvps" fill="#6C9BEB" radius={[6, 6, 0, 0]} name="RSVPs" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Row 4b: Stakeholder Mix & Regional Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {analytics.stakeholder_mix?.length > 0 && (
          <Card className="p-5" data-testid="analytics-stakeholder-mix">
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4">Stakeholder Mix</h3>
            <div className="h-56">
              {analytics.stakeholder_mix.length === 1 ? (
                <div className="h-full flex flex-col items-center justify-center">
                  <div className="text-3xl font-bold text-brand-navy tabular-nums">{analytics.stakeholder_mix[0].value}</div>
                  <div className="text-xs text-slate-500 mt-1">members &middot; {analytics.stakeholder_mix[0].name}</div>
                  <div className="text-[10px] text-slate-400 mt-2 px-2 text-center">More variety appears as members from different backgrounds join</div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={analytics.stakeholder_mix} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}>
                      {analytics.stakeholder_mix.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>
        )}
        {analytics.region_mix?.length > 0 && (
          <Card className="p-5" data-testid="analytics-region-mix">
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4">Regional Distribution</h3>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                {analytics.region_mix.length <= 10 ? (
                  <RadarChart data={analytics.region_mix} outerRadius={80}>
                    <PolarGrid stroke="#e2e8f0" />
                    <PolarAngleAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <PolarRadiusAxis tick={{ fontSize: 9 }} />
                    <Radar name="Members" dataKey="value" stroke="#1e3a5f" fill="#1e3a5f" fillOpacity={0.35} />
                    <Tooltip contentStyle={{ fontSize: 12 }} />
                  </RadarChart>
                ) : (
                  <PieChart>
                    <Pie data={analytics.region_mix.slice(0, 12)} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}>
                      {analytics.region_mix.slice(0, 12).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: 12 }} />
                  </PieChart>
                )}
              </ResponsiveContainer>
            </div>
          </Card>
        )}
      </div>

      {/* Row 5: Banner Ad Engagement */}
      {bannerAnalytics && bannerAnalytics.banners.length > 0 && (
        <div className="space-y-4" data-testid="analytics-banner-engagement">
          <div className="flex items-center gap-3 pt-2">
            <div className="h-px flex-1 bg-slate-200" />
            <h2 className="text-sm font-bold text-slate-700 uppercase tracking-widest">Banner Ad Performance</h2>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Card className="p-4 text-center">
              <p className="text-2xl font-bold text-brand-navy">{bannerAnalytics.total_impressions.toLocaleString()}</p>
              <p className="text-xs text-slate-500">Total Impressions</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-2xl font-bold text-brand-sage">{bannerAnalytics.total_clicks.toLocaleString()}</p>
              <p className="text-xs text-slate-500">Total Clicks</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-500">
                {bannerAnalytics.total_impressions > 0 ? ((bannerAnalytics.total_clicks / bannerAnalytics.total_impressions) * 100).toFixed(2) : 0}%
              </p>
              <p className="text-xs text-slate-500">Overall CTR</p>
            </Card>
          </div>

          <Card className="p-5">
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-3">Banner Performance</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-slate-500 uppercase">
                    <th className="pb-2 pr-4">Sponsor</th>
                    <th className="pb-2 pr-4">Type</th>
                    <th className="pb-2 pr-4">Placement</th>
                    <th className="pb-2 pr-4">Campaign</th>
                    <th className="pb-2 pr-4">Status</th>
                    <th className="pb-2 pr-4 text-right">Impressions</th>
                    <th className="pb-2 pr-4 text-right">Clicks</th>
                    <th className="pb-2 text-right">CTR</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {bannerAnalytics.banners.map(b => (
                    <tr key={b.banner_id} className="hover:bg-slate-50">
                      <td className="py-2 pr-4 font-medium">{b.sponsor_name}</td>
                      <td className="py-2 pr-4">{b.banner_type === 'top' ? 'Top' : 'Side'}</td>
                      <td className="py-2 pr-4 text-slate-500">{b.placement}</td>
                      <td className="py-2 pr-4 text-xs text-slate-500">
                        {b.campaign_start || b.campaign_end
                          ? `${b.campaign_start || '\u2014'} \u2192 ${b.campaign_end || '\u2014'}`
                          : <span className="text-slate-400">Always</span>}
                      </td>
                      <td className="py-2 pr-4">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          b.campaign_status === 'live' ? 'bg-green-100 text-green-700' :
                          b.campaign_status === 'upcoming' ? 'bg-blue-100 text-blue-700' :
                          b.campaign_status === 'expired' ? 'bg-slate-200 text-slate-600' :
                          'bg-red-100 text-red-600'
                        }`}>
                          {b.campaign_status?.charAt(0).toUpperCase() + b.campaign_status?.slice(1)}
                        </span>
                      </td>
                      <td className="py-2 pr-4 text-right">{b.impressions.toLocaleString()}</td>
                      <td className="py-2 pr-4 text-right">{b.clicks.toLocaleString()}</td>
                      <td className="py-2 text-right font-medium">{b.ctr}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* Row 6: BriteLabs Team Insights */}
      {labInsights && (
        <div className="space-y-6" data-testid="analytics-britelabs-insights">
          <div className="flex items-center gap-3 pt-2">
            <div className="h-px flex-1 bg-slate-200" />
            <h2 className="text-sm font-bold text-slate-700 uppercase tracking-widest">BriteLabs Team DNA</h2>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <div className="flex items-center gap-3">
            <Select value={selectedLab} onValueChange={setSelectedLab}>
              <SelectTrigger data-testid="lab-insights-selector" className="w-72">
                <SelectValue placeholder="Select a BriteLab..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All BriteLabs (Aggregate)</SelectItem>
                {labInsights.labs.map(lab => (
                  <SelectItem key={lab.lab_id} value={lab.lab_id}>{lab.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-slate-500">
              {labInsights.aggregate.total_participants} participants across {labInsights.aggregate.total_labs} labs
            </span>
          </div>

          {(() => {
            const isAggregate = selectedLab === 'all';
            const data = isAggregate
              ? labInsights.aggregate
              : labInsights.labs.find(l => l.lab_id === selectedLab)?.team_composition;
            const labDetail = !isAggregate ? labInsights.labs.find(l => l.lab_id === selectedLab) : null;

            if (!data) return null;

            return (
              <div className="space-y-6">
                {labDetail && (
                  <Card className="p-4 bg-brand-navy/5 border-brand-navy/10">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div>
                        <h4 className="font-bold text-slate-800">{labDetail.title}</h4>
                        <p className="text-xs text-slate-500">{labDetail.category} &middot; {labDetail.status}</p>
                      </div>
                      <div className="flex gap-4 text-center">
                        <div><p className="text-lg font-bold text-brand-navy">{labDetail.team_size}</p><p className="text-xs text-slate-500">Team</p></div>
                        <div><p className="text-lg font-bold text-brand-sage">{labDetail.discussions}</p><p className="text-xs text-slate-500">Discussions</p></div>
                        <div><p className="text-lg font-bold text-brand-blue">{labDetail.milestones_completed}/{labDetail.milestones_total}</p><p className="text-xs text-slate-500">Milestones</p></div>
                        <div><p className="text-lg font-bold text-amber-500">{labDetail.engagement_score}</p><p className="text-xs text-slate-500">Engagement</p></div>
                      </div>
                    </div>
                    {labDetail.members.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {labDetail.members.map((m, i) => (
                          <div key={i} className="flex items-center gap-2 bg-white rounded-full px-3 py-1 text-xs border border-slate-200">
                            <div className="w-5 h-5 rounded-full bg-gradient-to-r from-brand-navy to-brand-sage flex items-center justify-center text-white text-[10px] font-bold">
                              {m.name?.charAt(0)}
                            </div>
                            <span className="font-medium text-slate-700">{m.name}</span>
                            {m.sector && <span className="text-slate-400">&middot; {m.sector}</span>}
                            {m.trait_tags?.length > 0 && (
                              <span className="flex gap-1">
                                {m.trait_tags.map(t => (
                                  <span key={t} className={`px-1.5 py-0.5 rounded-full text-[9px] font-medium border ${TAG_COLORS[t] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>{t}</span>
                                ))}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </Card>
                )}

                {/* Behavioural Balance panel */}
                {(() => {
                  const behav = isAggregate ? labInsights.aggregate.behavioural : labDetail?.behavioural;
                  if (!behav || !behav.axes) return null;
                  return (
                    <Card className="p-5" data-testid="insights-behavioural">
                      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                        <div>
                          <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide">Behavioural Balance</h3>
                          <p className="text-xs text-slate-500 mt-0.5">{behav.participants_with_data} member{behav.participants_with_data === 1 ? '' : 's'} answered the behavioural profile</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500">Diversity Score</span>
                          <div className={`px-3 py-1 rounded-full text-sm font-bold ${
                            behav.diversity_score >= 70 ? 'bg-emerald-50 text-emerald-700' :
                            behav.diversity_score >= 40 ? 'bg-amber-50 text-amber-700' :
                            'bg-rose-50 text-rose-700'
                          }`} data-testid="diversity-score">
                            {behav.diversity_score}/100
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {behav.axes.map(axis => (
                          <div key={axis.axis_key} data-testid={`axis-${axis.axis_key}`}>
                            <div className="flex items-center justify-between text-xs font-semibold text-slate-600 mb-1.5">
                              <span>{axis.axis_label}</span>
                              <span className="text-slate-400 text-[10px] font-normal">{axis.total} answered</span>
                            </div>
                            <div className="flex h-7 rounded-full overflow-hidden border border-slate-200 bg-slate-50">
                              {axis.options.map((opt, i) => (
                                <div key={opt.label} className={`flex items-center justify-center text-[10px] font-semibold border-r border-white last:border-r-0 ${
                                  i === 0 ? (TAG_COLORS[opt.label]?.split(' ')[0] || 'bg-indigo-50') + ' ' + (TAG_COLORS[opt.label]?.split(' ')[1] || 'text-indigo-700') : (TAG_COLORS[opt.label]?.split(' ')[0] || 'bg-amber-50') + ' ' + (TAG_COLORS[opt.label]?.split(' ')[1] || 'text-amber-700')
                                }`} style={{ width: `${opt.pct || 0}%` }} title={`${opt.label}: ${opt.value} (${opt.pct}%)`}>
                                  {opt.pct >= 15 ? `${opt.label} ${opt.pct}%` : ''}
                                </div>
                              ))}
                            </div>
                            <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                              {axis.options.map(opt => (
                                <span key={opt.label}>{opt.label} ({opt.value})</span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                      {behav.missing_perspectives?.length > 0 && (
                        <div className="mt-4 p-3 bg-amber-50 border border-amber-100 rounded-lg" data-testid="missing-perspectives">
                          <p className="text-xs font-semibold text-amber-800 mb-1">⚠ Missing Perspectives</p>
                          <ul className="text-xs text-amber-700 space-y-0.5 list-disc list-inside">
                            {behav.missing_perspectives.map((m, i) => <li key={i}>{m}</li>)}
                          </ul>
                        </div>
                      )}
                    </Card>
                  );
                })()}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card className="p-5" data-testid="insights-personality">
                    <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4">Personality Types</h3>
                    {data.personality_types?.length > 0 ? (
                      <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={data.personality_types} cx="50%" cy="45%" innerRadius={40} outerRadius={70} dataKey="value" paddingAngle={2}>
                              {data.personality_types.map((_, i) => (
                                <Cell key={i} fill={COLORS[i % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                            <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: '11px' }} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="h-56 flex items-center justify-center text-slate-400 text-sm">No personality data</div>
                    )}
                  </Card>

                  <Card className="p-5" data-testid="insights-sectors">
                    <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4">Industry Sectors</h3>
                    {data.sectors?.length > 0 ? (
                      <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={data.sectors} layout="vertical" margin={{ left: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                            <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={110} />
                            <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                            <Bar dataKey="value" radius={[0, 6, 6, 0]} name="People">
                              {data.sectors.map((_, i) => (
                                <Cell key={i} fill={COLORS[i % COLORS.length]} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="h-56 flex items-center justify-center text-slate-400 text-sm">No sector data</div>
                    )}
                  </Card>

                  <Card className="p-5" data-testid="insights-experience">
                    <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4">Experience Levels</h3>
                    {data.experience_levels?.length > 0 ? (
                      <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={data.experience_levels}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                            <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                            <Bar dataKey="value" radius={[6, 6, 0, 0]} name="People">
                              {data.experience_levels.map((_, i) => (
                                <Cell key={i} fill={['#9EDE4B', '#9BC269', '#6C9BEB', '#3E60B1', '#121E30'][i % 5]} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="h-56 flex items-center justify-center text-slate-400 text-sm">No experience data</div>
                    )}
                  </Card>

                  <Card className="p-5" data-testid="insights-expertise">
                    <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4">Top Expertise</h3>
                    {data.expertise?.length > 0 ? (
                      <div className="h-56 overflow-y-auto">
                        <div className="space-y-2">
                          {data.expertise.map((item, i) => {
                            const maxVal = Math.max(...data.expertise.map(e => e.value));
                            return (
                              <div key={item.name} className="flex items-center gap-3">
                                <span className="text-xs text-slate-600 w-40 truncate flex-shrink-0" title={item.name}>{item.name}</span>
                                <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
                                  <div className="h-full rounded-full transition-all" style={{ width: `${(item.value / maxVal) * 100}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                                </div>
                                <span className="text-xs font-medium text-slate-500 w-6 text-right">{item.value}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="h-56 flex items-center justify-center text-slate-400 text-sm">No expertise data</div>
                    )}
                  </Card>

                  {/* Stakeholder Categories */}
                  <Card className="p-5" data-testid="insights-stakeholders">
                    <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4">Stakeholder Categories</h3>
                    {(data.stakeholder_categories || []).length > 0 ? (
                      <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={data.stakeholder_categories} cx="50%" cy="45%" innerRadius={40} outerRadius={70} dataKey="value" paddingAngle={2}>
                              {data.stakeholder_categories.map((_, i) => (
                                <Cell key={i} fill={COLORS[i % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                            <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: '11px' }} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="h-56 flex items-center justify-center text-slate-400 text-sm">No stakeholder data</div>
                    )}
                  </Card>

                  {/* Regions */}
                  <Card className="p-5" data-testid="insights-regions">
                    <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4">Regional Mix</h3>
                    {(data.regions || []).length > 0 ? (
                      <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                          {data.regions.length <= 8 ? (
                            <RadarChart data={data.regions} outerRadius={70}>
                              <PolarGrid stroke="#e2e8f0" />
                              <PolarAngleAxis dataKey="name" tick={{ fontSize: 10 }} />
                              <PolarRadiusAxis tick={{ fontSize: 9 }} />
                              <Radar name="Members" dataKey="value" stroke="#1e3a5f" fill="#1e3a5f" fillOpacity={0.35} />
                              <Tooltip />
                            </RadarChart>
                          ) : (
                            <PieChart>
                              <Pie data={data.regions.slice(0, 10)} cx="50%" cy="45%" innerRadius={40} outerRadius={70} dataKey="value" paddingAngle={2}>
                                {data.regions.slice(0, 10).map((_, i) => (
                                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip />
                              <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: '11px' }} />
                            </PieChart>
                          )}
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="h-56 flex items-center justify-center text-slate-400 text-sm">No regional data</div>
                    )}
                  </Card>
                </div>

                {isAggregate && labInsights.labs.length > 0 && (
                  <Card className="p-5" data-testid="insights-composition-engagement">
                    <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-1">Composition vs Engagement</h3>
                    <p className="text-xs text-slate-500 mb-4">How behavioural diversity correlates with team activity and progress</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200 text-xs text-slate-500 uppercase tracking-wide">
                            <th className="text-left py-2 pr-3">BriteLab</th>
                            <th className="text-center py-2 pr-3">Team</th>
                            <th className="text-center py-2 pr-3">Diversity</th>
                            <th className="text-center py-2 pr-3">Discussions</th>
                            <th className="text-center py-2 pr-3">Milestones</th>
                            <th className="text-center py-2 pr-3">Time to Complete</th>
                            <th className="text-center py-2">Engagement</th>
                          </tr>
                        </thead>
                        <tbody>
                          {labInsights.labs.map(lab => (
                            <tr key={lab.lab_id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                              onClick={() => setSelectedLab(lab.lab_id)}
                              data-testid={`composition-row-${lab.lab_id}`}>
                              <td className="py-2 pr-3">
                                <p className="font-medium text-slate-800 truncate max-w-[200px]" title={lab.title}>{lab.title}</p>
                                <p className="text-[10px] text-slate-400">{lab.category}</p>
                              </td>
                              <td className="text-center py-2 pr-3 text-slate-600">{lab.team_size}</td>
                              <td className="text-center py-2 pr-3">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                                  (lab.behavioural?.diversity_score || 0) >= 70 ? 'bg-emerald-50 text-emerald-700' :
                                  (lab.behavioural?.diversity_score || 0) >= 40 ? 'bg-amber-50 text-amber-700' :
                                  'bg-rose-50 text-rose-700'
                                }`}>{lab.behavioural?.diversity_score ?? '–'}</span>
                              </td>
                              <td className="text-center py-2 pr-3 text-slate-600">{lab.discussions}</td>
                              <td className="text-center py-2 pr-3">
                                <span className="text-slate-600">{lab.milestones_completed}/{lab.milestones_total}</span>
                                <span className="text-[10px] text-slate-400 ml-1">({lab.milestone_completion_pct}%)</span>
                              </td>
                              <td className="text-center py-2 pr-3 text-slate-500 text-xs">
                                {lab.time_to_complete_days != null ? `${lab.time_to_complete_days}d` : <span className="text-slate-300">—</span>}
                              </td>
                              <td className="text-center py-2 font-bold text-brand-navy">{lab.engagement_score}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                )}

                {isAggregate && labInsights.labs.length > 0 && (
                  <Card className="p-5" data-testid="insights-leaderboard">
                    <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4">Lab Engagement Leaderboard</h3>
                    <div className="space-y-2">
                      {labInsights.labs.map((lab, i) => (
                        <div key={lab.lab_id}
                          className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
                          onClick={() => setSelectedLab(lab.lab_id)}
                          data-testid={`leaderboard-lab-${lab.lab_id}`}>
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                            i === 0 ? 'bg-amber-100 text-amber-700' : i === 1 ? 'bg-slate-200 text-slate-600' : i === 2 ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-500'
                          }`}>
                            {i + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">{lab.title}</p>
                            <p className="text-xs text-slate-400">{lab.category} &middot; {lab.team_size} members &middot; {lab.discussions} discussions</p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-brand-navy">{lab.engagement_score}</p>
                            <p className="text-xs text-slate-400">score</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </div>
            );
          })()}
        </div>
      )}
    </>
  );
}
